import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { selectEmployees, fetchAllEmployees } from 'src/stores/employeeSlice';
import ApiClient from '../ApiClient';
import { notify } from "../utils/ToastUtils";
import { CircleAlert, ShieldAlert, Trash2, Search, X, ChevronLeft, ChevronRight, ArrowLeft, User } from 'lucide-react';
import ActionConfirmModal from './ActionConfirmModal';

const Reset2FAForm = ({ setCurrentView }) => {
    const dispatch = useDispatch();
    const employees = useSelector(selectEmployees) || [];
    const activeEmployees = employees.filter(el => el.STATUS === "Active");

    const containerRef = useRef(null);
    const topBarRef = useRef(null);

    // Selected employees list
    const [selectedEmployees, setSelectedEmployees] = useState([]);

    // Search and pagination state
    const [filterState, setFilterState] = useState({
        searchQuery: '',
        page: 1,
        rowsPerPage: 10
    });

    // Action and modal status
    const [actionStatus, setActionStatus] = useState({
        isDisabled: false,
        submitAttempted: false,
        isConfirmOpen: false
    });

    useEffect(() => {
        const scrollToTop = () => {
            if (topBarRef.current) {
                try {
                    topBarRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } catch (e) {
                    topBarRef.current.scrollIntoView(true);
                }
            } else if (containerRef.current) {
                try {
                    containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } catch (e) {
                    containerRef.current.scrollIntoView(true);
                }
            }

            // Scroll all ancestor scrollable elements to top
            let el = (topBarRef.current || containerRef.current)?.parentElement;
            while (el) {
                if (el.scrollHeight > el.clientHeight || el.scrollTop > 0) {
                    el.scrollTop = 0;
                    if (typeof el.scrollTo === 'function') {
                        el.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                }
                el = el.parentElement;
            }

            const scrollContainer = document.getElementById('main-scroll-container');
            if (scrollContainer) {
                scrollContainer.scrollTop = 0;
                scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
            window.scrollTo(0, 0);
            if (document.documentElement) document.documentElement.scrollTop = 0;
            if (document.body) document.body.scrollTop = 0;
        };

        scrollToTop();
        const t1 = setTimeout(scrollToTop, 50);
        const t2 = setTimeout(scrollToTop, 150);
        const t3 = setTimeout(scrollToTop, 300);

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
        };
    }, []);

    // Filter employees based on search query
    const filteredEmployees = useMemo(() => {
        if (!filterState.searchQuery) return activeEmployees;
        const lowerQuery = filterState.searchQuery.toLowerCase();
        return activeEmployees.filter(emp => 
            (emp.EMP_NAME && emp.EMP_NAME.toLowerCase().includes(lowerQuery)) ||
            (emp.EMP_ID && emp.EMP_ID.toLowerCase().includes(lowerQuery)) ||
            (emp.EMP_WORK_EMAIL && emp.EMP_WORK_EMAIL.toLowerCase().includes(lowerQuery)) ||
            (emp.DESIGNATION && emp.DESIGNATION.toLowerCase().includes(lowerQuery)) 
        );
    }, [activeEmployees, filterState.searchQuery]);

    // Pagination
    const totalPages = Math.ceil(filteredEmployees.length / filterState.rowsPerPage);
    const paginatedEmployees = useMemo(() => {
        const start = (filterState.page - 1) * filterState.rowsPerPage;
        return filteredEmployees.slice(start, start + filterState.rowsPerPage);
    }, [filteredEmployees, filterState.page, filterState.rowsPerPage]);

    // Handlers
    const toggleEmployeeSelection = (employee) => {
        setSelectedEmployees(prev => 
            prev.find(e => e.EMP_CODE === employee.EMP_CODE)
                ? prev.filter(e => e.EMP_CODE !== employee.EMP_CODE)
                : [...prev, employee]
        );
    };

    const removeSelectedEmployee = (empCode) => {
        setSelectedEmployees(prev => prev.filter(e => e.EMP_CODE !== empCode));
    };

    const clearAllSelections = () => {
        setSelectedEmployees([]);
    };

    const handleDiscard = () => {
        setSelectedEmployees([]);
        setFilterState(prev => ({ ...prev, searchQuery: '', page: 1 }));
        setActionStatus(prev => ({ ...prev, submitAttempted: false, isConfirmOpen: false }));
    };

    const handleGoBack = () => {
        handleDiscard();
        setCurrentView('main');
    };

    const reset2fa = async () => {
        setActionStatus(prev => ({ ...prev, submitAttempted: true }));

        if (selectedEmployees.length > 0) {
            setActionStatus(prev => ({ ...prev, isDisabled: true }));
            try {
                await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/admin/reset2fa`, {
                    employees: selectedEmployees?.map(e => e.EMP_CODE)
                });
                notify.success(`2FA Reset successfully for ${selectedEmployees.length} employees`);
                dispatch(fetchAllEmployees());
                handleDiscard();
                setActionStatus(prev => ({ ...prev, isDisabled: false, submitAttempted: false }));
            } catch (error) {
                console.log(error);
                let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later";
                notify.error(errmsg);
                setActionStatus(prev => ({ ...prev, isDisabled: false }));
            }
        }
    };

    // Helper for initials
    const getInitials = (name) => {
        if (!name) return 'U';
        const parts = name.trim().split(/\s+/);
        if (parts.length > 1) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.trim().substring(0, 2).toUpperCase();
    };

    // Get color based on initials (deterministic)
    const getAvatarColor = (name) => {
        const colors = [
            'bg-blue-100 text-blue-600',
            'bg-emerald-100 text-emerald-600',
            'bg-purple-100 text-purple-600',
            'bg-orange-100 text-orange-600',
            'bg-pink-100 text-pink-600',
            'bg-indigo-100 text-indigo-600'
        ];
        if (!name) return colors[0];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    return (
        <div ref={containerRef} style={{ scrollMarginTop: '120px' }} className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-200/80 p-4 xl:p-6 flex flex-col min-h-[550px] relative overflow-hidden scroll-mt-28">
            {/* Top Bar */}
            <div ref={topBarRef} style={{ scrollMarginTop: '120px' }} className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-3 border-b border-slate-100 pb-4 shrink-0 scroll-mt-28">
                <div className="flex items-center gap-3">
                    <button type="button" onClick={handleGoBack} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100/80 rounded-xl transition-all duration-300 -ml-2">
                        <ArrowLeft size={18} />
                    </button>
                    <div className="p-2.5 bg-gradient-to-br from-red-50 to-red-100/50 text-red-600 rounded-xl border border-red-100 shadow-sm shadow-red-100/50 ring-4 ring-white">
                        <ShieldAlert size={18} />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-slate-800 tracking-tight">Reset Two-Factor Authentication</h2>
                        <p className="text-xs text-slate-500 mt-0.5 font-medium">Disable 2FA for selected employees across the organization</p>
                    </div>
                </div>
                <div className="flex items-center gap-2.5">
                    <button type="button" className="px-4 py-2 border border-slate-200/80 text-slate-700 bg-white rounded-lg hover:bg-slate-50 hover:border-slate-300 hover:shadow-md transition-all duration-300 font-semibold text-xs flex items-center gap-1.5" onClick={handleDiscard}>
                        <Trash2 size={14} /> Discard
                    </button>
                    <button
                        type="button"
                        className={`px-4 py-2 bg-red-600 text-white rounded-lg
                            shadow-lg shadow-red-500/30
                            hover:shadow-red-500/50 hover:-translate-y-0.5
                            transition-all duration-300 font-semibold text-xs
                            flex items-center gap-1.5
                            ${actionStatus.isDisabled
                            ? '!opacity-50 cursor-not-allowed'
                            : '!opacity-100'
                            }`}
                         onClick={() => {
                            setActionStatus(prev => ({
                                ...prev,
                                submitAttempted: true,
                                ...(selectedEmployees.length > 0 ? { isConfirmOpen: true } : {})
                            }));
                        }}
                        disabled={actionStatus.isDisabled}
                        >
                        {actionStatus.isDisabled ? <><span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>Loading...</> : "Reset 2FA"}
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {actionStatus.submitAttempted && selectedEmployees.length === 0 && (
                <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-600 mb-4">
                    <CircleAlert size={16} />
                    <span>Please select at least one employee.</span>
                </div>
            )}

            {/* Main Content Layout */}
            <div className="flex flex-col lg:flex-row gap-4 flex-1">
                
                {/* Left Column: Selection Table */}
                <div className="flex-1 flex flex-col">
                    <div className="bg-white/80 backdrop-blur-md border border-slate-200/80 rounded-2xl flex flex-col flex-1 overflow-hidden shadow-sm shadow-slate-200/50">
                        {/* Search Bar */}
                        <div className="p-2.5 border-b border-slate-200/80 bg-white/50">
                            <div className="relative group">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                                <input 
                                    type="text"
                                    placeholder="Search by name, employee ID or email..."
                                    value={filterState.searchQuery}
                                    onChange={(e) => setFilterState(prev => ({ ...prev, searchQuery: e.target.value, page: 1 }))}
                                    className="w-full pl-9 pr-3 py-2 bg-white border border-slate-300/80 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all shadow-sm"
                                />
                            </div>
                        </div>

                        {/* Table Header */}
                        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                            <div className="col-span-1 flex items-center justify-center">
                                <input 
                                    type="checkbox" 
                                    className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-1 transition-all cursor-pointer"
                                    checked={paginatedEmployees.length > 0 && paginatedEmployees.every(emp => selectedEmployees.find(se => se.EMP_CODE === emp.EMP_CODE))}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            const newSelections = [...selectedEmployees];
                                            paginatedEmployees.forEach(emp => {
                                                if (!newSelections.find(se => se.EMP_CODE === emp.EMP_CODE)) {
                                                    newSelections.push(emp);
                                                }
                                            });
                                            setSelectedEmployees(newSelections);
                                        } else {
                                            setSelectedEmployees(selectedEmployees.filter(se => !paginatedEmployees.find(pe => pe.EMP_CODE === se.EMP_CODE)));
                                        }
                                    }}
                                />
                            </div>
                            <div className="col-span-6">Employee Name</div>
                            <div className="col-span-2">Employee ID</div>
                            <div className="col-span-3">Designation</div>
                        </div>

                        {/* Table Body */}
                        <div className="flex-1 overflow-y-auto bg-white/40 min-h-[250px]">
                            {paginatedEmployees.length > 0 ? (
                                paginatedEmployees.map(emp => {
                                    const isSelected = !!selectedEmployees.find(se => se.EMP_CODE === emp.EMP_CODE);
                                    return (
                                        <div 
                                            key={emp.EMP_CODE} 
                                            className={`grid grid-cols-12 gap-2 px-4 py-2 border-b border-slate-100/80 items-center transition-all duration-200 cursor-pointer hover:shadow-sm relative ${isSelected ? 'bg-blue-50/50' : 'hover:bg-slate-50/80'}`}
                                            onClick={() => toggleEmployeeSelection(emp)}
                                        >
                                            {isSelected && <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-r-full"></div>}
                                            <div className="col-span-1 flex items-center justify-center">
                                                <input 
                                                    type="checkbox" 
                                                    checked={isSelected}
                                                    onChange={() => {}} // handled by row click
                                                    className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer pointer-events-none transition-all"
                                                />
                                            </div>
                                            <div className="col-span-6 flex items-center gap-2.5">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 shadow-sm border border-white/50 ${getAvatarColor(emp.EMP_NAME)}`}>
                                                    {getInitials(emp.EMP_NAME)}
                                                </div>
                                                <span className="text-[13px] text-slate-800 font-semibold truncate">{emp.EMP_NAME}</span>
                                            </div>
                                            <div className="col-span-2 text-[13px] text-slate-500 truncate">
                                                {emp.EMP_ID}
                                            </div>
                                            <div className="col-span-3 text-[13px] text-slate-500 truncate">
                                                {emp.DESIGNATION || '-'}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full text-slate-500 py-10">
                                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                                        <Search size={20} className="text-slate-400" />
                                    </div>
                                    <p className="text-xs font-medium text-slate-600">No employees found</p>
                                    <p className="text-[10px] text-slate-400 mt-1">Try adjusting your search criteria</p>
                                </div>
                            )}
                        </div>

                        {/* Pagination */}
                        <div className="p-2.5 border-t border-slate-200/80 bg-white/50 backdrop-blur-sm flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-600">
                            <div className="font-medium text-slate-500">
                                Showing {Math.min((filterState.page - 1) * filterState.rowsPerPage + 1, filteredEmployees.length)}-{Math.min(filterState.page * filterState.rowsPerPage, filteredEmployees.length)} of {filteredEmployees.length}
                            </div>
                            
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1">
                                    <button 
                                        className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:hover:text-slate-400 transition-colors"
                                        onClick={() => setFilterState(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                                        disabled={filterState.page === 1}
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    
                                    <div className="flex items-center gap-1">
                                        {(() => {
                                            let start = 1;
                                            let end = totalPages;
                                            if (totalPages > 5) {
                                                if (filterState.page <= 3) {
                                                    end = 5;
                                                } else if (filterState.page >= totalPages - 2) {
                                                    start = totalPages - 4;
                                                } else {
                                                    start = filterState.page - 2;
                                                    end = filterState.page + 2;
                                                }
                                            }
                                            const pages = [];
                                            for (let i = start; i <= end; i++) pages.push(i);
                                            
                                            return (
                                                <>
                                                    {pages.map(pageNum => (
                                                        <button 
                                                            key={pageNum}
                                                            onClick={() => setFilterState(prev => ({ ...prev, page: pageNum }))}
                                                            className={`w-6 h-6 flex items-center justify-center rounded text-[10px] font-semibold transition-colors ${filterState.page === pageNum ? 'bg-blue-100 text-blue-600 border border-blue-200' : 'text-slate-600 hover:bg-slate-100'}`}
                                                        >
                                                            {pageNum}
                                                        </button>
                                                    ))}
                                                    {totalPages > 5 && filterState.page < totalPages - 2 && (
                                                        <>
                                                            <span className="text-slate-400 text-[10px] px-0.5">...</span>
                                                            <button 
                                                                onClick={() => setFilterState(prev => ({ ...prev, page: totalPages }))}
                                                                className="w-6 h-6 flex items-center justify-center rounded text-[10px] font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
                                                            >
                                                                {totalPages}
                                                            </button>
                                                        </>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>

                                    <button 
                                        className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:hover:text-slate-400 transition-colors"
                                        onClick={() => setFilterState(prev => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))}
                                        disabled={filterState.page === totalPages || totalPages === 0}
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Selected List */}
                <div className="lg:w-[300px] shrink-0 flex flex-col bg-white/80 backdrop-blur-md border border-slate-200/80 rounded-2xl shadow-sm shadow-slate-200/50 overflow-hidden mt-6 lg:mt-0 max-h-full">
                    <div className="flex items-center justify-between p-3 border-b border-slate-200/80 bg-slate-50/50">
                        <div className="flex items-center gap-2">
                            <h3 className="font-bold text-slate-800 text-xs">Selected</h3>
                            <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold">{selectedEmployees.length}</span>
                        </div>
                        {selectedEmployees.length > 0 && (
                            <button 
                                onClick={clearAllSelections}
                                className="text-[10px] font-bold text-slate-400 hover:text-red-600 transition-colors"
                            >
                                Clear All
                            </button>
                        )}
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-3 bg-white/40 space-y-2">
                        {selectedEmployees.length > 0 ? (
                            selectedEmployees.map(emp => (
                                <div key={emp.EMP_CODE} className="flex items-center justify-between p-2 bg-white border border-slate-200/60 rounded-xl hover:shadow-md hover:-translate-y-0.5 hover:border-slate-300/80 transition-all duration-300 group">
                                    <div className="flex items-center gap-2 overflow-hidden">
                                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 shadow-sm border border-white/50 ${getAvatarColor(emp.EMP_NAME)}`}>
                                            {getInitials(emp.EMP_NAME)}
                                        </div>
                                        <div className="min-w-0">
                                             <p className="text-sm font-semibold text-slate-800 truncate">{emp.EMP_NAME}</p>
                                            <p className="text-[10px] font-medium text-slate-500 truncate">{emp.EMP_ID}</p>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => removeSelectedEmployee(emp.EMP_CODE)}
                                        className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all shrink-0 focus:opacity-100"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400 py-10 text-center">
                                <div className="w-12 h-12 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center mb-3">
                                    <User size={20} className="text-slate-300" />
                                </div>
                                <p className="text-sm font-semibold text-slate-500">No employees selected</p>
                                <p className="text-[11px] mt-1 text-slate-400 max-w-[180px]">Select employees from the list to reset their 2FA credentials</p>
                            </div>
                        )}
                    </div>
                </div>

            </div>

            {/* Bottom Alert */}
            <div className="mt-4 p-3 bg-gradient-to-r from-orange-50 to-orange-50/30 border border-orange-200/80 rounded-xl flex items-start gap-2 shadow-sm">
                <CircleAlert className="text-orange-500 shrink-0 mt-0.5" size={16} />
                <div>
                    <p className="text-xs text-orange-900 font-bold leading-relaxed">Warning: Immediate Action Required</p>
                    <p className="text-[11px] text-orange-800 font-medium leading-relaxed mt-0.5">
                        2FA will be immediately disabled for the selected employees. They will be required to configure a new 2FA method upon their next login.
                    </p>
                </div>
            </div>

            <ActionConfirmModal
                isOpen={actionStatus.isConfirmOpen}
                onClose={() => setActionStatus(prev => ({ ...prev, isConfirmOpen: false }))}
                onConfirm={reset2fa}
                loading={actionStatus.isDisabled}
                title="Confirm 2FA Reset"
                message={`Are you sure you want to reset Two-Factor Authentication for ${selectedEmployees.length} selected employee(s)?\nThey will be forced to reconfigure 2FA on their next login.`}
                type="danger"
                confirmText="Yes, Reset 2FA"
            />
        </div>
    );
};

export default Reset2FAForm;
